import { useCallback, useState } from 'react';

import { Text } from '../../../components/UI-CHAKRA/Text';
import { Button } from '../../../components/UI-CHAKRA/Button';
import { FormLabel } from '../../../components/UI-CHAKRA/FormLabel';
import { Checkbox } from '../../../components/UI-CHAKRA/Checkbox';
import { Flex } from '../../../components/UI-CHAKRA/Flex';

import Dialog from '../../../components/Dialogs/BaseDialog';

import { useIsMobile } from '../../../helpers/useIsMobile';
import { Box } from '../../../components/UI-CHAKRA/Box';

interface DialogAskForPrintOrDigitalCertificateProps {
  onPrintAndSign: () => void;
  onDigitalCertificate: () => void;
  open: boolean;
  onClose: () => void;
  onNotShowAgain: () => void;
}

export function DialogAskForPrintOrDigitalCertificate(props: DialogAskForPrintOrDigitalCertificateProps) {
  const { onClose, onNotShowAgain, onPrintAndSign, onDigitalCertificate } = props;
  const [open, setOpen] = useState(props.open);
  const [notShowAgain, setNotShowAgain] = useState(false);
  const isMobile = useIsMobile();

  const handleClose = useCallback(() => {
    setOpen(false);
    onClose();
  }, [onClose]);

  const handlePrintAndSign = useCallback(() => {
    if (notShowAgain) {
      onNotShowAgain();
    }
    setOpen(false);
    onPrintAndSign();
  }, [notShowAgain, onNotShowAgain, onPrintAndSign]);

  const handleDigitalCertificate = useCallback(() => {
    setOpen(false);
    onDigitalCertificate();
  }, [onDigitalCertificate]);

  const onChangeNotShowAgain = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setNotShowAgain(event.target.checked);
  }, []);

  return (
    <Dialog
      isOpen={open}
      onClose={handleClose}
      width={{ base: '95%', md: '790px' }}
      stylesCustom={{
        borderRadius: '30px',
        padding: '40px',
      }}
    >
      <Flex flexDirection="column" gap="10px" data-testid="ask-for-print-text">
        <Box bgColor="#FFDEDE" borderRadius="10px" p="10px" justifyContent="center">
          <Text fontSize="18px" fontWeight={700} color="gray.dark" textAlign={'center'}>
            Evite desperdício em papel e apoie a transformação digital
          </Text>
        </Box>

        <Flex flexDirection={'column'} gap={'20px'} px={'40px'}>
          <Text>
            <b>Documento médico assinado digitalmente não precisa ser impresso.</b>
          </Text>

          <Text>
            Seus pacientes receberão os documentos automaticamente <b>via celular por SMS, WhatsApp ou E-mail</b>.
          </Text>

          <Text>
            A receita digital auxilia na adesão ao tratamento de forma mais prática e segura de forma digital.
          </Text>

          <Text>
            Instrua seus pacientes a abrirem a receita através do link que chegará pelo celular. Com isso eles:
          </Text>

          <Box px={{ base: '20px' }}>
            <ul style={{ listStyle: 'inherit' }}>
              <li>Terão a receita médica sempre acessível no celular;</li>
              <li>Não perderão a receita ou esquecerão o histórico do tratamento;</li>
              <li>Poderão adquirir seus medicamentos online ou em qualquer farmácia do Brasil;</li>
              <li>Poderão aproveitar promoções e benefícios exclusivos.</li>
            </ul>
          </Box>
        </Flex>

        <Flex mt="10px" gap="10px" justifyContent="space-between">
          <Button
            onClick={handlePrintAndSign}
            width="fit-content"
            borderRadius={'20px'}
            fontSize={'14px'}
            px="20px"
            py="10px"
            data-testid="ask-for-print-cancel"
            variant="outline"
            color={'red'}
            borderColor={'red'}
          >
            {isMobile ? 'Voltar' : 'Ignorar benefícios'}
          </Button>
          <Button
            onClick={handleDigitalCertificate}
            variant="outline"
            width="fit-content"
            borderRadius={'20px'}
            fontSize={'14px'}
            fontWeight={700}
            px="20px"
            py="10px"
            flexWrap="wrap"
            wordBreak="break-all"
            data-testid="ask-for-print-procced"
            bgColor={'green'}
            borderColor={'green'}
            color={'white'}
          >
            {isMobile ? 'Imprimir' : 'Seguir somente digital'}
          </Button>
        </Flex>
        <Flex justifyContent="center" mt="10px">
          <FormLabel display="flex" alignItems="center" justifyContent={'center'}>
            <Checkbox checked={notShowAgain} onChange={onChangeNotShowAgain}>
              <Text fontSize={'12px'} fontWeight={'400'} color="gray.dark">
                Não exibir novamente
              </Text>
            </Checkbox>
          </FormLabel>
        </Flex>
      </Flex>
    </Dialog>
  );
}
